/**
 * An agent-built app (slice 6i §3.3, §7): the app in a sandboxed iframe that
 * fills the page, loaded from a one-hour ticket, and its ⋯ menu. The page in
 * the iframe has no network; its `window.crew` asks this page, which answers
 * only its own iframe, only the methods in `bridge.ts`, each with a size cap,
 * and only messages carrying this render's token. A write tells the iframe
 * to refetch; a new version live reopens it. The frame loads once per
 * version: a renewed ticket only serves the bridge.
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { api, errorHint } from "../../app/api";
import { useApp } from "./api";
import { AppMenu } from "./app-menu";
import { answerCall, bridgeMethods, parseCall, type Opened } from "./bridge";

/** A ticket lasts an hour: a page open longer gets a fresh one, a little before. */
const REOPEN_MS = 50 * 60_000;

// fallow-ignore-next-line complexity -- a screen: checked in the browser at 390 and 1440 px and by e2e/apps.spec.ts, not unit tests
export function AppPage({ appId }: { appId: string }) {
  const frame = useRef<HTMLIFrameElement>(null);
  // Each render of the frame gets its own token, which every call must carry.
  const [token, setToken] = useState(() => crypto.randomUUID());
  const opened = useQuery({
    // Not under ["app", appId]: the hub's hint refetches those, and a new ticket would reload the frame.
    queryKey: ["app-open", appId, token],
    queryFn: () => api<Opened>("POST", `/api/apps/${appId}/open`),
    staleTime: REOPEN_MS,
    refetchInterval: REOPEN_MS,
    retry: false,
  });
  const detail = useApp(appId);
  const o = opened.data;

  useEffect(() => {
    if (!o) return;
    // The page's origin is opaque, so it can't be named: the window and the token are the check.
    const toFrame = (message: Record<string, unknown>) =>
      frame.current?.contentWindow?.postMessage({ __crew: 1, ...message }, "*");
    const methods = bridgeMethods({
      opened: o,
      api: (method, path, body) => api(method, path, body),
      fetchFile: (url) => fetch(url),
      changed: () => toFrame({ event: "change" }),
    });
    function onMessage(event: MessageEvent) {
      if (!frame.current || event.source !== frame.current.contentWindow) return;
      const call = parseCall(event.data, token);
      if (call) void answerCall(methods, call).then(toFrame);
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [o, token]);

  // The hub's `app` hint: someone changed its data, or a new version is live.
  const live = useQuery({
    queryKey: ["app", appId, "live"],
    queryFn: () => Date.now(),
    staleTime: Number.POSITIVE_INFINITY,
  });
  const seen = useRef(0);
  useEffect(() => {
    const at = live.dataUpdatedAt;
    if (seen.current && at !== seen.current)
      frame.current?.contentWindow?.postMessage({ __crew: 1, event: "change" }, "*");
    seen.current = at;
  }, [live.dataUpdatedAt]);
  const liveVersion = detail.data?.app.version;
  useEffect(() => {
    if (o && liveVersion !== undefined && liveVersion !== o.app.version)
      setToken(crypto.randomUUID());
  }, [o, liveVersion]);

  // The frame loads once per token: a renewed ticket (or any refetch) serves the bridge's
  // file reads without reloading the app and losing what's on screen.
  const [first, setFirst] = useState<{ token: string; url: string } | null>(null);
  if (o && first?.token !== token) setFirst({ token, url: o.url });
  const shownUrl = first?.token === token ? first.url : null;
  const src = useMemo(() => (shownUrl ? `${shownUrl}#t=${token}` : null), [shownUrl, token]);
  const app = detail.data?.app ?? o?.app;

  if (opened.error)
    return (
      <p className="p-6 text-sm text-muted-foreground" role="alert">
        {errorHint(opened.error)}
      </p>
    );
  if (!o || !src || !app) return <p className="p-6 text-sm text-muted-foreground">Opening…</p>;

  return (
    <div className="relative flex h-full min-h-dvh flex-col md:min-h-full">
      <AppMenu app={app} />
      <iframe
        ref={frame}
        key={token}
        title={app.title}
        src={src}
        // The same as the response's CSP sandbox: scripts and forms, no same-origin.
        sandbox="allow-scripts allow-forms"
        allow="screen-wake-lock"
        className="w-full flex-1 border-0 bg-background"
      />
    </div>
  );
}
